import useSWR from 'swr';

import { FEATURES } from 'appConstants';
import { useFeatureFlags } from 'hooks';
import { IDetailResponse, IPaginatedResponse } from 'utils/types';

import { fetcher } from './utils';

export interface ICustomerLoyaltyProgramTier {
  id: number;
  forced_membership_tier_expires_at: string;
  forced_only: boolean;
  images: {
    url: string;
    section: string;
  }[];
  minimum_accumulation_value: number;
  name: string;
  reason: string;
}

export interface ICustomerLoyaltyProgram {
  id: number;
  state: string;
  name: string;
  customer: {
    id: number;
    user_membership_account_id: number;
    state: string;
    current_state: string;
    forced_state: string;
    forced_state_expires_at: null;
    tier: ICustomerLoyaltyProgramTier;
    next_tier: ICustomerLoyaltyProgramTier;
    forced_tier: ICustomerLoyaltyProgramTier | null;
    current_cycle_tier: ICustomerLoyaltyProgramTier;
    point_balance: number;
    tier_points: number;
    lifetime_point_balance: number;
    total_redeemed_points: number;
    total_points_transfer: number;
    total_points_received: number;
    joined_at: string;
    membership_begins_at: string;
    membership_expires_at: string;
  };
  tiers: ICustomerLoyaltyProgramTier[];
  ordering: number;
  enrolled_customers: number;
  issued_date: string;
  updated_at: string;
  current_loyalty_review_cycle_starts_at: string;
  current_loyalty_review_cycle_ends_at: string;
}

export const useCustomer = (id?: number | string | null, params?: Record<string, any>) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IDetailResponse<any>>(
    id ? { url: `/v4/dash/customers/${id}`, params } : null,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useCustomerList = (params: Record<string, any> = {}, shouldFetch: boolean = true) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<any>>(
    shouldFetch ? { url: '/v4/dash/customers', params } : null,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCustomerWithToken = (
  customerId?: number | string | null,
  token?: string | null
) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IDetailResponse<any>>(
    customerId && token
      ? {
          url: `/v4/dash/customers/${customerId}`,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          external: true
        }
      : null,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useCustomerLoyaltyProgramList = (
  customerId: number | null | undefined,
  params: object
) => {
  const key = customerId
    ? { url: `/v4/dash/customers/${customerId}/loyalty_programs`, params }
    : null;

  const { data, isLoading, error, mutate } = useSWR<IPaginatedResponse<ICustomerLoyaltyProgram>>(
    key,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate };
};

export const useCustomerLoyaltyProgram = (customerId: number, loyaltyProgramId: number) => {
  const { data, isLoading, mutate } = useSWR<IDetailResponse<ICustomerLoyaltyProgram>>(
    `/v4/dash/customers/${customerId}/loyalty_programs/${loyaltyProgramId}`,
    fetcher
  );

  return { data: data?.data, isLoading, mutate };
};

export interface LoyaltyProgramTransaction {
  id: number;
  transaction_id: number;
  event_name: string;
  event_id: null;
  expires_at: null;
  transacted_amount: string;
  transacted_at: string;
  points: number;
  transaction_type: string;
  deleted: false;
  properties: Record<string, any>;
}

export const useCustomerLoyaltyProgramTransactionList = (
  customerId: number,
  loyaltyProgramId: number,
  params
) => {
  const url = `v4/dash/customers/${customerId}/loyalty_programs/${loyaltyProgramId}/loyalty_transactions`;

  const { data, isLoading, mutate } = useSWR<IPaginatedResponse<ICustomerLoyaltyProgram>>(
    { url, params },
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, mutate };
};

export const useCustomerRequestLoyaltyProgram = (
  customerId: number,
  loyaltyProgramId: number,
  params
) => {
  const url = `v4/dash/customers/${customerId}/loyalty_programs/${loyaltyProgramId}/customer_requests`;

  const { data, isLoading, mutate } = useSWR({ url, params: { status: 'pending' } }, fetcher);

  return { data: data?.data, meta: data?.meta, isLoading, mutate };
};
